import { lazy, type ComponentType, type LazyExoticComponent } from "react";

export const CHUNK_RELOAD_KEY = "page-has-been-force-refreshed";
export const CHUNK_RELOAD_PENDING_KEY = "chunk-reload-pending";

function getErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object" && "message" in error) {
    const { message } = error as { message: unknown };
    return typeof message === "string" ? message : "";
  }
  return "";
}

/**
 * Is Chunk Load Error.
 */
export function isChunkLoadError(
  error: unknown,
  options?: { fromViteAssetScript?: boolean }
): boolean {
  const message = getErrorMessage(error).toLowerCase();
  if (!message) return false;

  if (
    message.includes("importing a module script failed") ||
    message.includes("importing binding name") ||
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("loading chunk") ||
    message.includes("chunkloaderror")
  ) {
    return true;
  }

  if (options?.fromViteAssetScript && message.includes("load failed")) {
    return true;
  }

  return false;
}

export function clearChunkReloadFlag(): void {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  sessionStorage.removeItem(CHUNK_RELOAD_PENDING_KEY);
}

export function clearChunkReloadPendingFlag(): void {
  sessionStorage.removeItem(CHUNK_RELOAD_PENDING_KEY);
}

export function attemptOneTimeReload(): boolean {
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === "true") return false;

  sessionStorage.setItem(CHUNK_RELOAD_KEY, "true");
  sessionStorage.setItem(CHUNK_RELOAD_PENDING_KEY, "true");
  window.location.reload();
  return true;
}

export function tryRecoverFromChunkLoadError(
  error: unknown,
  options?: { fromViteAssetScript?: boolean }
): boolean {
  if (!isChunkLoadError(error, options)) return false;
  return attemptOneTimeReload();
}

export function tryRecoverFromDynamicImportFailure(): boolean {
  return attemptOneTimeReload();
}

/**
 * Lazy With Retry.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (error) {
      if (tryRecoverFromChunkLoadError(error)) {
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}

function isViteAssetScript(target: EventTarget | null): target is HTMLScriptElement {
  return (
    target instanceof HTMLScriptElement &&
    typeof target.src === "string" &&
    target.src.includes("/assets/")
  );
}

/**
 * Setup Chunk Load Recovery.
 */
export function setupChunkLoadRecovery(): void {
  if (typeof window === "undefined") return;

  window.addEventListener(
    "error",
    (event) => {
      if (isViteAssetScript(event.target)) {
        tryRecoverFromChunkLoadError(event.error ?? event.message ?? "Loading chunk failed", {
          fromViteAssetScript: true,
        });
        return;
      }

      tryRecoverFromChunkLoadError(event.error ?? event.message);
    },
    true
  );

  window.addEventListener("unhandledrejection", (event) => {
    tryRecoverFromChunkLoadError(event.reason);
  });
}
